import React, { useState } from 'react';
import { X, Download, Calendar, FileText } from 'lucide-react';

const ExportModal = ({ isOpen, onClose, onExportAll, onExportCurrentMonth, onExportCustomMonth }) => {
  const [exportType, setExportType] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date()?.getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date()?.getFullYear());

  // Don't render if modal is not open
  if (!isOpen) return null;

  const handleExport = () => {
    if (exportType === 'all') {
      onExportAll?.();
    } else if (exportType === 'current') {
      onExportCurrentMonth?.();
    } else if (exportType === 'custom') {
      onExportCustomMonth?.(selectedMonth, selectedYear);
    }
    // Close modal after export
    onClose?.();
  };

  const handleBackdropClick = (e) => {
    // Close modal when clicking on backdrop
    if (e?.target === e?.currentTarget) {
      onClose?.();
    }
  };

  const handleModalContentClick = (e) => {
    // Prevent clicks inside modal from closing it
    e?.stopPropagation();
  };

  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date()?.getFullYear() - i);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-md"
        onClick={handleModalContentClick}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Download className="w-5 h-5" />
            Exportar Despesas
          </h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Export Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Exportação
            </label>
            
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setExportType('all')}
                className={`w-full p-3 border-2 rounded-lg transition-all text-left ${
                  exportType === 'all' ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-700" />
                  <div>
                    <div className="font-medium text-gray-900">Todas as Despesas</div>
                    <div className="text-xs text-gray-500">Exportar lista completa</div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setExportType('current')}
                className={`w-full p-3 border-2 rounded-lg transition-all text-left ${
                  exportType === 'current' ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-700" />
                  <div>
                    <div className="font-medium text-gray-900">Mês Atual</div>
                    <div className="text-xs text-gray-500">
                      {months?.[new Date()?.getMonth()]} {new Date()?.getFullYear()}
                    </div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setExportType('custom')}
                className={`w-full p-3 border-2 rounded-lg transition-all text-left ${
                  exportType === 'custom' ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-700" />
                  <div>
                    <div className="font-medium text-gray-900">Mês Específico</div>
                    <div className="text-xs text-gray-500">Escolha mês e ano</div>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Custom Month Selection */}
          {exportType === 'custom' && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mês
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e?.target?.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {months?.map((month, index) => (
                      <option key={index} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ano
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e?.target?.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {years?.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Format Info */}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-600">
              📄 Os dados serão exportados em formato CSV, compatível com Excel e Google Sheets
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-700"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;